import { isRecord } from "@arti/utilities";
import { useCallback, useState } from "react";

export interface RestartInstanceResult {
  previousRunId: string | null;
  status: "already_killed" | "ok";
}

export interface RestartInstanceOptions {
  alsoStopRunningSpawns?: boolean;
}

export class InstanceLifecycleActionError extends Error {
  readonly error: string | null;
  readonly retryable: boolean;
  readonly status: number;

  constructor(input: {
    error: string | null;
    message: string;
    retryable: boolean;
    status: number;
  }) {
    super(input.message);
    this.name = "InstanceLifecycleActionError";
    this.error = input.error;
    this.retryable = input.retryable;
    this.status = input.status;
  }
}

export interface WakeIfPendingResult {
  dispatched: boolean;
}

export async function requestRestartInstance(
  instanceId: string,
  options: RestartInstanceOptions = {},
): Promise<RestartInstanceResult> {
  const body = await postLifecycleAction(
    `/user/instances/${instanceId}/restart`,
    { also_stop_running_spawns: options.alsoStopRunningSpawns === true },
  );
  if (
    !isRecord(body) ||
    (body.status !== "ok" && body.status !== "already_killed")
  ) {
    throw new Error("Restart response was malformed");
  }
  return {
    previousRunId:
      typeof body.previous_run_id === "string" ? body.previous_run_id : null,
    status: body.status,
  };
}

export async function requestWakeIfPending(
  instanceId: string,
): Promise<WakeIfPendingResult> {
  const body = await postLifecycleAction(
    `/user/instances/${instanceId}/wakeup-if-pending`,
  );
  if (!isRecord(body) || typeof body.dispatched !== "boolean") {
    throw new Error("Wake-if-pending response was malformed");
  }
  return { dispatched: body.dispatched };
}

export function useRestartInstance() {
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const restartInstance = useCallback(
    async (instanceId: string, options: RestartInstanceOptions = {}) => {
      setPending(true);
      setErrorMessage(null);
      try {
        return await requestRestartInstance(instanceId, options);
      } catch (error) {
        setErrorMessage(formatLifecycleError(error, "Restart failed"));
        throw error;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  return { error: errorMessage, pending, restartInstance };
}

export function useWakeIfPending() {
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const wakeIfPending = useCallback(async (instanceId: string) => {
    setPending(true);
    setErrorMessage(null);
    try {
      return await requestWakeIfPending(instanceId);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Wake-if-pending failed";
      setErrorMessage(message);
      throw error;
    } finally {
      setPending(false);
    }
  }, []);

  return { error: errorMessage, pending, wakeIfPending };
}

async function postLifecycleAction(
  url: string,
  requestBody?: Record<string, unknown>,
): Promise<unknown> {
  const response = await fetch(url, {
    ...(requestBody === undefined
      ? {}
      : {
          body: JSON.stringify(requestBody),
          headers: { "content-type": "application/json" },
        }),
    credentials: "include",
    method: "POST",
  });
  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    throw new InstanceLifecycleActionError({
      error: readLifecycleErrorCode(responseBody),
      message:
        readLifecycleError(responseBody) ??
        `Request failed: ${response.status}`,
      retryable: readLifecycleRetryable(responseBody),
      status: response.status,
    });
  }
  return responseBody;
}

function formatLifecycleError(error: unknown, fallback: string): string {
  if (error instanceof InstanceLifecycleActionError) {
    return error.retryable ? `${error.message} You can retry.` : error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

export function readLifecycleError(body: unknown): string | null {
  if (!isRecord(body)) {
    return null;
  }
  if (typeof body.message === "string" && body.message.trim().length > 0) {
    return body.message;
  }
  if (typeof body.error === "string" && body.error.trim().length > 0) {
    return body.error;
  }
  return null;
}

function readLifecycleErrorCode(body: unknown): string | null {
  if (!isRecord(body)) {
    return null;
  }
  return typeof body.error === "string" && body.error.trim().length > 0
    ? body.error
    : null;
}

function readLifecycleRetryable(body: unknown): boolean {
  return isRecord(body) && body.retryable === true;
}
